import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { theme, toRem } from 'design';

interface ISidebarProps {
  children?: ReactNode;
  className?: string;
  fixedMobile?: boolean; // for a mobile looking action sheet
  isOpen?: boolean; // used to specify open/close a fixedMobile
}

const MOBILE_TOP_OFFSET = theme.header.height;

const Layout = styled.aside<ISidebarProps>`
  position: relative;
  z-index: ${theme.zIndex.sidebar};
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: ${theme.sidebar.width.small};
  padding: 0;
  background-color: ${theme.color.neutral[50]};
  transition: all ${theme.transition.nav};

  ${({ fixedMobile }) =>
    // apply actionsheet-y styles when true
    // styles change at media query by standard styles below (line 62)
    fixedMobile &&
    css`
      position: fixed;
      top: calc(100% - ${toRem(MOBILE_TOP_OFFSET)});
      right: 0;
      bottom: 0;
      border-radius: ${toRem(22)} ${toRem(22)} 0 0;
      box-shadow:
        0px -9px 49px rgba(0, 0, 0, 0.05),
        0px -2.71324px 14.7721px rgba(0, 0, 0, 0.0325794),
        0px -1.12694px 6.13556px rgba(0, 0, 0, 0.025),
        0px -0.407592px 2.21911px rgba(0, 0, 0, 0.0174206);

      @media (min-width: ${theme.breakpoint.small}px) {
        padding: 0 0 0 ${toRem(theme.nav.width.closed)};
      }
    `}

  ${({ fixedMobile, isOpen }) =>
    fixedMobile &&
    isOpen &&
    css`
      top: ${toRem(theme.header.height + 44)};
      bottom: 0;
      overflow: auto;
    `}

  @media (min-width: ${theme.breakpoint.small}px) {
    border-radius: 0;
  }

  @media (min-width: ${theme.breakpoint.large}px) {
    position: fixed;
    top: 0;
    right: 0;
    width: ${toRem(theme.sidebar.width.large)};
    height: 100%;
    padding: ${toRem(theme.header.height)} 0 0;
    border-radius: 0;
    border-left: ${toRem(1)} solid ${theme.color.neutral[200]};
    box-shadow: none;
    overflow: auto;

    ${({ isOpen }) =>
      isOpen &&
      css`
        top: 0;
        bottom: auto;
      `}
  }

  @media (min-width: ${theme.breakpoint.xlarge}px) {
    width: ${toRem(theme.sidebar.width.xlarge)};
  }
`;

const ContentContainer = styled.div<ISidebarProps>`
  position: relative;
  display: flex;
  flex-grow: 1;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  height: auto;
  min-height: 100%;
  padding-bottom: ${toRem(16)};

  @media (min-width: ${theme.breakpoint.large}px) {
    padding-bottom: 0;
  }
`;

const Sidebar = ({ fixedMobile, isOpen, children, className }: ISidebarProps) => (
  <Layout className={className} fixedMobile={fixedMobile} isOpen={isOpen}>
    <ContentContainer isOpen={isOpen}>{children}</ContentContainer>
  </Layout>
);

export default Sidebar;
